import React from 'react';
import isArray from 'lodash/isArray';
import {
  parse as parseQuery,
  // eslint-disable-next-line import/no-extraneous-dependencies
} from 'query-string';

import cloneItem from '../../helpers/cloneItem';

import Slider from '../../components/FilterSliderComponent';

const getValueForFieldDefault = value => (
  value && value.length
  ? value
  : undefined
);

export const filterPagination = ['FilterPagination', {
  id: 'pagination',
  section: 'bottom',
}];

export const filterPopulate = ['FilterHidden', {
  id: 'populate',
  section: 'bottom',
  build: builder => builder.add('populate', {
    customer: { name: 1 },
    store: { name: 1 },
  }),
}];

export const filterSort = ['FilterField', {
  id: 'sort',
  label: null,
  section: 'top',
  VALUES_MAP: {
    createdDesc: { createdAt: -1 },
    createdAsc: { createdAt: 1 },
    updatedDesc: { updatedAt: -1 },
    updatedAsc: { updatedAt: 1 },
    ratingAsc: { rating: 1 },
    ratingDesc: { rating: -1 },
    productAsc: { productName: 1 },
    productDesc: { productName: -1 },
  },
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value]
      ? builder.add('sort', { ...filter.VALUES_MAP[value] })
      : builder.add('sort', { ...filter.VALUES_MAP.createdDesc })
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldSelect', {
    name: 'sort',
    placeholder: 'Sort',
    defaultValue: 'createdDesc',
    initialValue: 'createdDesc',
    prepareValueForInput: value => (value ? value : 'createdDesc'),
    choices: [{
      label: '↓ Date Created',
      value: 'createdDesc',
    }, {
      label: '↑ Date Created',
      value: 'createdAsc',
    }, {
      label: '↓ Date Updated',
      value: 'updatedDesc',
    }, {
      label: '↑ Date Updated',
      value: 'updatedAsc',
    }, {
      label: '↑ Rating',
      value: 'ratingAsc',
    }, {
      label: '↓ Rating',
      value: 'ratingDesc',
    }, {
      label: '↑ Product',
      value: 'productAsc',
    }, {
      label: '↓ Product',
      value: 'productDesc',
    }],
  }],
}];

export const filterSearch = ['FilterField', {
  id: 'search',
  label: null, // 'ID',
  section: 'top',
  buildQuery: (value, builder) => value && builder.add(
    'where',
    {
      OR: [
        { name: { REGEX: value, OPTIONS: 'i' } },
      ],
    }
  ),
  getValueForField: value => value || '',
  getValueForQuery: (value) => {
    value = !value
      ? undefined
      : value.target
        ? value.target.value
        : value;
    return !value || !value.length
      ? undefined
      : value;
  },
  field: ['FieldText', {
    name: 'search',
    placeholder: 'Search',
  }],
}];

export const filterOrder = ['FilterField', {
  id: 'order',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client, location: props.location }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { order: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'order',
    url: '/orders',
    placeholder: 'Order',
    // mode: 'multiple',
    allowClear: true,
    extraQuerySortProps: { createdAt: -1 },
    getExtraQueryConditions: (props) => {
      const result = [{
        status: 'DELIVERED',
      }];
      if (props?.location?.search) {
        const { store } = parseQuery(props?.location?.search);
        if (store) {
          result.push({ store });
        }
      }
      return result;
    },
    searchPaths: ['_id'],
  }],
}];

export const filterProduct = ['FilterField', {
  id: 'product',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { product: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'product',
    url: '/products?scopeToSellerStore=true',
    placeholder: 'Product',
    // mode: 'multiple',
    allowClear: true,
    extraQuerySortProps: { name: 1 },
    searchPaths: ['name'],
  }],
}];

export const filterCustomer = ['FilterField', {
  id: 'customer',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { customer: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'customer',
    url: '/customers',
    placeholder: 'Customer',
    // mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
  }],
}];

export const filterRating = ['FilterField', {
  id: 'rating',
  label: null,
  section: 'top',
  buildQuery: (value, builder) => {
    if (typeof value === 'string' && value.includes(',')) {
      value = value.split(',').map(str => parseInt(str, 10));
    }
    if (value && value.length === 2) {
      builder.add(
        'where',
        { rating: { GTE: parseInt(value[0], 10) } },
      );
      builder.add(
        'where',
        { rating: { LTE: parseInt(value[1], 10) } },
      );
    }
  },
  getValueForField: value => (
    Array.isArray(value)
      ? value
      : typeof value === 'string' && value.includes(',')
      ? value.split(',').map(str => parseInt(str, 10))
      : undefined
  ),
  field: ['FieldReact', {
    name: 'rating',
    label: null,
    // eslint-disable-next-line arrow-body-style
    renderContent: (props) => {
      return (
        <Slider
          label="Rating"
          value={props.value}
          onChange={props.onChange}
        />
      );
    },
  }],
}];

export const filterStore = ['FilterField', {
  id: 'store',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { store: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'store',
    url: '/stores/list',
    placeholder: 'Stores',
    mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
    // getChoiceLabel: choice => `${(choice.data.name)}`,
  }],
}];

export const ADMIN = [
  ...[
    [filterSort, 12],
    [filterStore, 12],

    [filterProduct, 8],
    [filterOrder, 8],
    [filterCustomer, 8],
    [filterRating, 24],
  ].map((config) => cloneItem(...config)),

  filterPopulate,
  filterPagination,
];

export const SELLER = [
  ...[
    [filterProduct, 12],
    [filterSort, 12],

    [filterOrder, 12],
    [filterCustomer, 12],
  ].map((config) => cloneItem(...config)),

  filterPopulate,
  filterPagination,
];
